/**
 * Payment domain models for Stripe-backed bounty payouts.
 *
 * Defines PaymentStatus (internal) and StripePaymentStatus (Stripe intent
 * statuses), the StripePayment record, StripePaymentDetails returned when
 * processing a payment, and PaymentBountySubmission, which bundles a payment
 * with its bounty and submission for display. Consumed by api/userPayment.ts,
 * api/admin.ts, the payments page, and the payment-method-created handler.
 */
import { Bounty } from "./bounty";
import { SubmissionDisplay } from "./submission";
import { CreatedUpdatedAt } from "./createdUpdatedAt";

export enum PaymentStatus {
    PENDING = "pending",
    SUCCEEDED = "succeeded",
    FAILED = "failed",
    CANCELLED = "cancelled",
}

export const PaymentStatuses: readonly PaymentStatus[] = [
    PaymentStatus.PENDING,
    PaymentStatus.SUCCEEDED,
    PaymentStatus.FAILED,
    PaymentStatus.CANCELLED,
];

export enum StripePaymentStatus {
    SUCCEEDED = "succeeded",
    PROCESSING = "processing",
    CANCELED = "canceled",
    FAILED = "failed",
    REQUIRES_ACTION = "requires_action",
    REQUIRES_CONFIRMATION = "requires_confirmation",
    REQUIRES_PAYMENT_METHOD = "requires_payment_method",
    REQUIRES_CAPTURE = "requires_capture",
}

export const StripePaymentStatuses: readonly StripePaymentStatus[] = [
    StripePaymentStatus.SUCCEEDED,
    StripePaymentStatus.PROCESSING,
    StripePaymentStatus.CANCELED,
    StripePaymentStatus.FAILED,
    StripePaymentStatus.REQUIRES_ACTION,
    StripePaymentStatus.REQUIRES_CONFIRMATION,
    StripePaymentStatus.REQUIRES_PAYMENT_METHOD,
    StripePaymentStatus.REQUIRES_CAPTURE,
];

export interface StripePayment extends CreatedUpdatedAt {
    id: number;
    senderUserId: number;
    receiverUserId: number;
    bountyId: number;
    submissionId: number;

    chargeAmount: number;
    payoutAmount: number;
    feeAmount: number;

    status: PaymentStatus;
    paymentIntentId: string | null;
    paymentMethodId: string;
    paymentIdempotencyKey?: string | null;

    paymentDate: string | null;
    paymentError: string | null;
    paymentErrorCode: string | null;
    paymentErrorMessage: string | null;
}

export interface StripePaymentDetails {
    stripePaymentId: number;
    paymentIntentId: string | null;
    stripePaymentStatus: StripePaymentStatus;
    amountCharged: number;
    platformFee: number;
    payoutAmount: number;
    paymentIdempotencyKey: string;
    clientSecret: string | null;
    nextAction: any | null;
    paymentErrorMessage: string | null;
}

export interface PaymentBountySubmission {
    payment: StripePayment;
    submission: SubmissionDisplay;
    bounty: Bounty;
}
